import { Button, Grid, styled, Box, Typography, useTheme, alpha, Paper, Divider } from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import { useFormik } from 'formik';
import { useEffect, useState, type FC } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import AppTextField from 'components/input-fields/AppTextField';
import { Toaster } from 'react-hot-toast';
import { DepartmentEntity, PublicDocumentEntity, SeasonEntity, SegmentEntity } from 'api/generated';
import FileUploadInput from 'components/input-fields/FileUploadInput';
import SelectTable from 'page-sections/public-document/selectTableForModal';
import { useSeason } from 'contexts/SeasonContext';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// component props interface
interface ModalProps {
    open: boolean;
    onClose: () => void;
    departments: DepartmentEntity[];
    segments: SegmentEntity[];
    onSumbit: (data: any, file: File) => void,
    onUpdate: (data: any) => void,
    data?: PublicDocumentEntity
}

// styled components
const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 1000,
    minWidth: 200,
    [theme.breakpoints.down('md')]: { 
        maxWidth: '95vw',
        padding: theme.spacing(2),
    },
    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 1.5,
    border: `1px solid ${theme.palette.divider}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[2],
    },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const AddPublicDocumentModal: FC<ModalProps> = ({
    open,
    onClose,
    onSumbit,
    onUpdate,
    segments,
    data,
    departments
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [file, setFile] = useState<File>();
    const [selectedDepartments, setSelectedDepartments] = useState<DepartmentEntity[]>([]);
    const [allDepartments, setAllDepartments] = useState(true);
    const [selectedSegments, setSelectedSegments] = useState<SegmentEntity[]>([]);
    const [allSegments, setAllSegments] = useState(true);
    const [selectedSeasons, setSelectedSeasons] = useState<SeasonEntity[]>([]);
    const [allSeasons, setAllSeasons] = useState(true);
    const { seasons } = useSeason();

    useEffect(() => {
        if (data)
            setData();
        else
            resetData();
    }, [open, data]);

    const resetData = () => {
        setSelectedDepartments([]);
        setAllDepartments(true);
        setSelectedSegments([])
        setAllSegments(true);
        setSelectedSeasons([]);
        setAllSeasons(true);
        resetForm();
        setFile(undefined);
    }

    const setData = () => {
        setSelectedDepartments(data?.departments ?? []);
        setAllDepartments(!((data?.departments?.length ?? 0) > 0));
        setSelectedSegments(data?.segments ?? [])
        setAllSegments(!((data?.segments?.length ?? 0) > 0));
        setSelectedSeasons(data?.seasons ?? []);
        setAllSeasons(!((data?.seasons?.length ?? 0) > 0));

    }



    const setSelectedDepartmentsFunction = (updatedDepartments: DepartmentEntity[]) => {
        if (updatedDepartments.find(x => x.id === 0)) {
            setAllDepartments(!allDepartments);
            setSelectedDepartments([]);
        }
        else {
            setSelectedDepartments(updatedDepartments);

        }
    }

    const setSelectedSeasonsFunction = (updatedSeasons: SeasonEntity[]) => {
        if (updatedSeasons.find(x => x.id === 0)) {
            setAllSeasons(!allSeasons);
            setSelectedSeasons([]);
        }
        else {
            setSelectedSeasons(updatedSeasons);

        }
    }

    const setSelectedSegmetnsFunction = (updatedSegments: SegmentEntity[]) => {
        if (updatedSegments.find(x => x.id === 0)) {
            setAllSegments(!allSegments);
            setSelectedSegments([]);
        }
        else {
            setSelectedSegments(updatedSegments);
            /*    const concatDepartments = departments.filter(department => !selectedDepartments.includes(department) && updatedSegments.map(x => x.id).includes(department.segmentId))
                if (concatDepartments.length > 0) {
                    setAllDepartments(false);
                    setSelectedDepartments([...selectedDepartments.concat(concatDepartments)]);
                } 
            */
            // remove departments that are altready selected as part of the selected segments
            const filtredDepartments = [...selectedDepartments.filter(department => !updatedSegments.map(s => s.id).includes(department.segmentId))];
            setSelectedDepartments(filtredDepartments);
        }
    }



    const initialValues = {
        name: ''
    };

    const fieldValidationSchema = Yup.object().shape({
        name: !data ? Yup.string().required(t('value.notValid')) : Yup.string()
    });

    const handleClose = () => {
        resetForm();
        onClose();
    }

    const { handleSubmit, resetForm, setFieldValue, touched, errors } = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: (values) => {
            if (file && !data) {
                onSumbit({
                    name: values.name,
                    departmentIds: !allDepartments ? selectedDepartments.map(x => x.id) : undefined,
                    segmentIds: !allSegments ? selectedSegments.map(x => x.id) : undefined,
                    seasonIds: !allSeasons ? selectedSeasons.map(x => x.id) : undefined,

                } as any, file);
                onClose()
            }
            else if (data) {
                onUpdate(
                    {
                        departmentIds: !allDepartments ? selectedDepartments.map(x => x.id) : [],
                        segmentIds: !allSegments ? selectedSegments.map(x => x.id) : [],
                        seasonIds: !allSeasons ? selectedSeasons.map(x => x.id) : [],

                    } as any
                );
                onClose();
            }
        },
    });

    return (
        <StyledAppModal open={open} handleClose={() => {
            handleClose();
        }}>
            <Toaster />

            <form onSubmit={handleSubmit}>
                {/* Header Section */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 3,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <DescriptionIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                            {!data 
                                ? t('common.tables.button.addItem', { item: t('publicDocuments.title') || 'Public Document' })
                                : t('common.tables.button.changeItem', { item: t('publicDocuments.title') || 'Public Document' })
                            }
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {!data 
                                ? t('publicDocuments.totalDocuments') || 'Add a new public document'
                                : t('publicDocuments.update') || 'Update document visibility'
                            }
                        </Typography>
                    </Box>
                </Box>

                {/* Form Fields Section */}
                {!data && (
                    <Box sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <AppTextField
                                    label={t('status.name') || 'Name'}
                                    fullWidth
                                    size='small'
                                    onChange={(e) => {
                                        setFieldValue('name', e.target.value);
                                    }}
                                    helperText={touched.name && errors.name}
                                    error={Boolean(touched.name && errors.name)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    border: `1px dashed ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    p: 2,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                    }
                                }}>
                                    <FileUploadInput
                                        maxDimension={30}
                                        handleOnChange={(e) => {
                                            if (e.target.files)
                                                setFile(e.target.files[0]);
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Selection Sections */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                        {t('publicDocuments.update') || 'Visibility Settings'}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <SectionCard elevation={0}>
                                <SectionHeader>
                                    <Box sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.info.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <CategoryIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight="600">
                                        {t('segment.itemNamePlural') || 'Segments'}
                                    </Typography>
                                </SectionHeader>
                                <Box sx={{ 
                                    maxHeight: 300, 
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bgcolor: alpha(theme.palette.divider, 0.1),
                                        borderRadius: 1,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: theme.palette.divider,
                                        borderRadius: 1,
                                        '&:hover': {
                                            bgcolor: theme.palette.text.secondary,
                                        },
                                    },
                                }}>
                                    <SelectTable
                                        data={[{ id: 0, name: t('common.all') }].concat(segments)}
                                        selectedData={selectedSegments}
                                        setSelectedData={setSelectedSegmetnsFunction}
                                        allOptionsSelected={allSegments}
                                        header={t('segment.itemNamePlural') || 'Segments'}
                                    />
                                </Box>
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <SectionCard elevation={0}>
                                <SectionHeader>
                                    <Box sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <BusinessIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight="600">
                                        {t('department.itemNamePlural') || 'Departments'}
                                    </Typography>
                                </SectionHeader>
                                <Box sx={{ 
                                    maxHeight: 300, 
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bgcolor: alpha(theme.palette.divider, 0.1),
                                        borderRadius: 1,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: theme.palette.divider,
                                        borderRadius: 1,
                                        '&:hover': {
                                            bgcolor: theme.palette.text.secondary,
                                        },
                                    },
                                }}>
                                    <SelectTable
                                        data={[{ id: 0, name: t('common.all') }].concat(departments)}
                                        selectedData={selectedDepartments}
                                        setSelectedData={setSelectedDepartmentsFunction}
                                        allOptionsSelected={allDepartments}
                                        selectedSegmentIds={selectedSegments.map(s => s.id)}
                                        header={t('department.itemNamePlural') || 'Departments'}
                                    />
                                </Box>
                            </SectionCard>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <SectionCard elevation={0}>
                                <SectionHeader>
                                    <Box sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <CalendarTodayIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight="600">
                                        {t('season.itemNamePlural') || 'Seasons'}
                                    </Typography>
                                </SectionHeader>
                                <Box sx={{ 
                                    maxHeight: 300, 
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bgcolor: alpha(theme.palette.divider, 0.1),
                                        borderRadius: 1,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: theme.palette.divider,
                                        borderRadius: 1,
                                        '&:hover': {
                                            bgcolor: theme.palette.text.secondary,
                                        },
                                    },
                                }}>
                                    <SelectTable
                                        data={[{ id: 0, name: t('common.all') }].concat(seasons)}
                                        selectedData={selectedSeasons}
                                        setSelectedData={setSelectedSeasonsFunction}
                                        allOptionsSelected={allSeasons}
                                        header={t('season.itemNamePlural') || 'Seasons'}
                                    />
                                </Box>
                            </SectionCard>
                        </Grid>
                    </Grid>
                </Box>

                {/* Action Buttons */}
                <Divider sx={{ my: 3 }} />
                <FlexBox justifyContent='flex-end' gap={2}>
                    <Button 
                        variant='outlined' 
                        onClick={() => {
                            handleClose();
                        }}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        {t('common.forms.button.cancel')}
                    </Button>
                    <Button 
                        type='submit' 
                        variant='contained' 
                        disabled={!file && !data}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: theme.shadows[2],
                            '&:hover': {
                                boxShadow: theme.shadows[4],
                            },
                        }}
                    >
                        {t('common.forms.button.save')}
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal>
    );
};

export default AddPublicDocumentModal;